import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Star } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "@/config/company";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-steel">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-16 md:px-10 md:py-20 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-4">
          <Link to="/" data-testid="footer-logo" className="flex items-center gap-3">
            <img
              src="/logo-mark.png"
              alt="MMC S.r.l. — Carpenteria Metallica"
              className="h-12 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="border-l border-white/15 pl-3 font-tech text-[9px] uppercase leading-tight tracking-[0.3em] text-white/50">
              S.r.l.
              <br />
              {COMPANY.sector}
            </span>
          </Link>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-fog">{COMPANY.claim}</p>
          <p
            data-testid="footer-google-rating"
            className="mt-4 inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.2em] text-white/50"
          >
            <Star className="size-3.5 shrink-0 text-molten" fill="currentColor" /> {COMPANY.google.rating} su Google ·{" "}
            {COMPANY.google.count} recensioni
          </p>
          <p className="mt-4 max-w-xs font-tech text-[10px] uppercase leading-relaxed tracking-[0.2em] text-white/40">
            {COMPANY.emailDisplay} — {COMPANY.phoneDisplay}
          </p>
        </div>

        <nav className="lg:col-span-2" aria-label="Navigazione footer">
          <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">Navigazione</p>
          <ul className="mt-5 space-y-2.5">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className="text-sm text-white/75 transition-colors duration-300 hover:text-molten"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="lg:col-span-3">
          <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">Servizi</p>
          <ul className="mt-5 space-y-2.5">
            {SERVICES.slice(0, 5).map((s) => (
              <li key={s.id}>
                <Link
                  to="/servizi"
                  data-testid={`footer-service-${s.id}`}
                  className="text-sm text-white/75 transition-colors duration-300 hover:text-molten"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">Contatti</p>
          <ul className="mt-5 space-y-3.5 text-sm text-white/75">
            <li className="flex items-start gap-3" data-testid="footer-phone">
              <Phone className="mt-0.5 size-4 shrink-0 text-molten" />
              {COMPANY.phoneHref ? (
                <a
                  href={COMPANY.phoneHref}
                  data-testid="footer-phone-link"
                  className="transition-colors duration-300 hover:text-molten"
                >
                  {COMPANY.phoneDisplay}
                </a>
              ) : (
                <span>{COMPANY.phoneDisplay}</span>
              )}
            </li>
            <li className="flex items-start gap-3" data-testid="footer-email">
              <Mail className="mt-0.5 size-4 shrink-0 text-molten" />
              {COMPANY.emailHref ? (
                <a
                  href={COMPANY.emailHref}
                  data-testid="footer-email-link"
                  className="transition-colors duration-300 hover:text-molten"
                >
                  {COMPANY.emailDisplay}
                </a>
              ) : (
                <span>{COMPANY.emailDisplay}</span>
              )}
            </li>
            <li className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="mt-0.5 size-4 shrink-0 text-molten" /> {COMPANY.addressDisplay}
            </li>
          </ul>
          <Link
            to="/contatti"
            data-testid="footer-cta"
            className="mt-7 inline-block bg-molten px-5 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-molten-hot"
          >
            Richiedi un preventivo
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-5 py-6 font-tech text-[10px] uppercase tracking-[0.2em] text-white/40 md:flex-row md:items-center md:px-10 lg:px-16">
          <span data-testid="footer-legal-line">
            © {year} MMC S.r.l. — Tutti i diritti riservati · {COMPANY.legal.piva}
          </span>
          <Link
            to="/informazioni-legali"
            data-testid="footer-link-legali"
            className="transition-colors duration-300 hover:text-molten"
          >
            Informazioni legali · Privacy · Cookie
          </Link>
        </div>
      </div>
    </footer>
  );
}
